import { useState } from 'react';
import {
  App,
  Button,
  Card,
  Dropdown,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps, MenuProps } from 'antd';
import {
  CopyOutlined,
  EyeOutlined,
  MoreOutlined,
  PlusOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { useEnterprises } from '@/hooks/useEnterprises';
import { useEnrollmentTokens, useRevokeEnrollmentToken } from '@/hooks/useEnrollment';
import type { EnrollmentTokenDto } from '@/types/enrollment';
import { CreateEnrollmentTokenModal } from './CreateEnrollmentTokenModal';
import { ViewQrModal } from './ViewQrModal';

dayjs.extend(relativeTime);
dayjs.locale('es');

const { Title, Text } = Typography;

export function EnrollmentPage() {
  const { message } = App.useApp();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [includeExpired, setIncludeExpired] = useState(false);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [enterpriseFilter, setEnterpriseFilter] = useState<number | undefined>();

  const [createOpen, setCreateOpen] = useState(false);
  const [viewQrToken, setViewQrToken] = useState<EnrollmentTokenDto | null>(null);

  const { data, isLoading, refetch, isFetching } = useEnrollmentTokens({
    page,
    pageSize,
    search: search || undefined,
    includeExpired,
    includeRevoked,
    enterpriseId: enterpriseFilter,
  });

  const { data: enterprises } = useEnterprises({ pageSize: 100 });
  const revokeMut = useRevokeEnrollmentToken();

  const handleRevoke = async (t: EnrollmentTokenDto) => {
    try {
      await revokeMut.mutateAsync({ id: t.id, reason: 'Revocado manualmente desde panel' });
      message.success('Token revocado.');
    } catch {
      // interceptor
    }
  };

  const handleCopyToken = (t: EnrollmentTokenDto) => {
    navigator.clipboard.writeText(t.tokenValue);
    message.success('Token copiado al portapapeles.');
  };

  const handleCopyUrl = (t: EnrollmentTokenDto) => {
    if (t.qrCodeUrl) {
      navigator.clipboard.writeText(t.qrCodeUrl);
      message.success('URL copiada al portapapeles.');
    }
  };

  const columns: TableProps<EnrollmentTokenDto>['columns'] = [
    {
      title: 'Token',
      key: 'token',
      render: (_, t) => (
        <Space direction="vertical" size={0}>
          <Space>
            <QrcodeOutlined style={{ color: 'var(--color-primary)' }} />
            <Text strong>{t.label || `Token #${t.id}`}</Text>
            {t.oneTimeOnly && <Tag color="orange">Un solo uso</Tag>}
          </Space>
          <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
            {t.tokenValue.slice(0, 24)}...
          </Text>
        </Space>
      ),
    },
    {
      title: 'Enterprise',
      dataIndex: 'enterpriseName',
      key: 'enterpriseName',
      width: 180,
    },
    {
      title: 'Perfil',
      dataIndex: 'profileName',
      key: 'profileName',
      width: 180,
      render: (v) => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary" italic>—</Text>,
    },
    {
      title: 'Usos',
      dataIndex: 'usedCount',
      key: 'usedCount',
      width: 90,
      align: 'center',
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: 'Estado',
      key: 'state',
      width: 130,
      render: (_, t) => {
        if (t.isRevoked) return <Tag color="error">Revocado</Tag>;
        if (t.isExpired) return <Tag color="default">Expirado</Tag>;
        if (t.oneTimeOnly && t.usedCount > 0) return <Tag color="warning">Usado</Tag>;
        return <Tag color="success">Activo</Tag>;
      },
    },
    {
      title: 'Expira',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      width: 180,
      render: (v) => (
        <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm')}>
          <Text>{dayjs(v).fromNow()}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_, t) => {
        const items: MenuProps['items'] = [
          {
            key: 'view',
            label: 'Ver QR',
            icon: <EyeOutlined />,
            disabled: !t.isUsable,
            onClick: () => setViewQrToken(t),
          },
          {
            key: 'copy-token',
            label: 'Copiar token',
            icon: <CopyOutlined />,
            onClick: () => handleCopyToken(t),
          },
          ...(t.qrCodeUrl ? [{
            key: 'copy-url',
            label: 'Copiar URL de enrolamiento',
            icon: <CopyOutlined />,
            onClick: () => handleCopyUrl(t),
          }] : []),
          { type: 'divider' as const },
          ...(t.isUsable ? [{
            key: 'revoke',
            label: 'Revocar',
            icon: <StopOutlined />,
            danger: true,
            onClick: () => handleRevoke(t),
          }] : []),
        ];

        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <QrcodeOutlined style={{ marginRight: 12 }} />
          Enrolamiento
        </Title>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => setCreateOpen(true)}
        >
          Generar QR
        </Button>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <Space wrap>
            <Input
              placeholder="Buscar por etiqueta..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => setPage(1)}
              style={{ width: 240 }}
              allowClear
            />
            <Select
              placeholder="Filtrar por Enterprise"
              allowClear
              value={enterpriseFilter}
              onChange={(v) => { setEnterpriseFilter(v); setPage(1); }}
              options={(enterprises?.items ?? []).map((e) => ({ value: e.id, label: e.displayNameEs }))}
              style={{ width: 240 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refrescar
            </Button>
          </Space>

          <Space wrap>
            <Text>Expirados:</Text>
            <Switch checked={includeExpired} onChange={(v) => { setIncludeExpired(v); setPage(1); }} />
            <Text>Revocados:</Text>
            <Switch checked={includeRevoked} onChange={(v) => { setIncludeRevoked(v); setPage(1); }} />
          </Space>
        </div>

        <Table<EnrollmentTokenDto>
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total: data?.totalCount ?? 0,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `${total} en total`,
          }}
        />
      </Card>

      <CreateEnrollmentTokenModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(t) => {
          setCreateOpen(false);
          setViewQrToken(t);
        }}
      />

      <ViewQrModal
        token={viewQrToken}
        onClose={() => setViewQrToken(null)}
      />
    </div>
  );
}
